import React, { useEffect, useState } from 'react';
import './_NewUser.css';
import { UsersModel } from '../models/UsersModel';
import {
    UserCreateModel,
    ActivityStatus,
    LeadType,
    SelectListItem,
    BranchModel,
    OfficeDepartmentModel,
} from '../models/superbit-api-models';
import TreeSelectInput, { TreeNode } from '../components/_TreeSelectInput';

// React port of Views/SystemManagement/Modals/_NewUser.cshtml.
// Handles both "add" (no userId) and "edit" (userId set). Create and edit both
// post through userCreate — the server upserts by Id, exactly like the old form
// which always posted to User/Create.

const EMPTY_PASSWORD = '******'; // Constants.EMPTY_PASSWORD sentinel = "unchanged"

// SystemRolesIds GUIDs (lowercased to compare against server-sent role ids).
const ROLE = {
    ADMIN: '4a0a7e1b-7e16-4134-b8b7-de6ef7ad2aec',
    BRANCH_DEPARTMENT_MANAGER: '4a153f9a-a9b8-4fb6-8c9a-a1e902ef4df8',
    PROJECT_MANAGER: '0422c570-481e-4cca-a6d8-c1549360b62e',
    PC_USER: '734c1f37-c66c-4147-95ea-3d02983d54ca',
    TABLET_USER: '68b03f05-029b-4a67-9201-d9db361b4cee',
    SELECTED_USER: '505c5455-82e3-4bb3-9b9a-4430b3def042',
    SALES_USER: 'e5ae8ebb-9f84-434e-a86e-0047a1349ab4',
    SALES_USER_ADMIN: 'c039800a-cd22-4106-b779-1b446b81091d',
    OFFICE_DEPARTMENT_MANAGER: 'e026af6f-52f0-40b6-b12f-94c546da1725',
};

const LEADS_ROLES = [
    ROLE.PC_USER,
    ROLE.TABLET_USER,
    ROLE.BRANCH_DEPARTMENT_MANAGER,
    ROLE.PROJECT_MANAGER,
    ROLE.SELECTED_USER,
];
const LEAD_MANAGER_ROLES = [ROLE.BRANCH_DEPARTMENT_MANAGER];
const MANAGER_ROLES = [
    ROLE.OFFICE_DEPARTMENT_MANAGER,
    ROLE.BRANCH_DEPARTMENT_MANAGER,
    ROLE.PROJECT_MANAGER,
    ROLE.ADMIN,
];
// Roles that require a sales-person / branch selection (SalesPersonValidation / BranchValidation).
const SALESPERSON_REQUIRED = [ROLE.SELECTED_USER, ROLE.TABLET_USER, ROLE.PC_USER, ROLE.BRANCH_DEPARTMENT_MANAGER];
const BRANCH_REQUIRED = [ROLE.SELECTED_USER, ROLE.TABLET_USER, ROLE.PC_USER];

const LEAD_TYPE_OPTIONS = [
    { value: LeadType._0, label: 'חדש' },
    { value: LeadType._1, label: 'משומש' },
    { value: LeadType._2, label: 'סלקט' },
];

const lc = (v?: string) => (v || '').toLowerCase();
const inGroup = (group: string[], roleId?: string) => group.includes(lc(roleId));

type Props = {
    show: boolean;
    userId?: string; // undefined = add, set = edit
    onClose: () => void;
    onSaved: () => void;
};

type FormState = {
    id?: string;
    fullName: string;
    title: string;
    email: string;
    emailPass: string;
    password: string;
    confirmPassword: string;
    phoneNumber: string;
    ipAddresses: string;
    status: ActivityStatus;
    roleId: string;
    link: string;
    leadTypes: LeadType[];
    branchId?: string;
    branchName?: string;
    officeDepartmentId?: string;
    officeDepartmentName?: string;
    salesPersonId: string;
    agentId: string;
    whatsAppNumber: string;
    extension: string;
};

const emptyForm = (): FormState => ({
    fullName: '',
    title: '',
    email: '',
    emailPass: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    ipAddresses: '',
    status: ActivityStatus._0,
    roleId: '',
    link: '',
    leadTypes: [],
    salesPersonId: '',
    agentId: '',
    whatsAppNumber: '',
    extension: '',
});

const toTreeNodes = (items?: (BranchModel | OfficeDepartmentModel)[]): TreeNode[] =>
    (items || []).map((i) => ({ id: i.id, name: i.name, parentId: i.parentId }));

const NewUser = (props: Props) => {
    const { show, userId, onClose, onSaved } = props;
    const isEdit = !!userId;

    const [usersModel] = useState<UsersModel>(() => new UsersModel());

    const [form, setForm] = useState<FormState>(emptyForm());
    const [roles, setRoles] = useState<SelectListItem[]>([]);
    const [salesPeople, setSalesPeople] = useState<SelectListItem[]>([]);
    const [agents, setAgents] = useState<SelectListItem[]>([]);
    const [branches, setBranches] = useState<TreeNode[]>([]);
    const [officeDepartments, setOfficeDepartments] = useState<TreeNode[]>([]);

    const [errors, setErrors] = useState<{ [k: string]: string }>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [serverError, setServerError] = useState<string>('');

    // Password reveal toggles (hidden in edit mode — passwords show as "******").
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showEmailPass, setShowEmailPass] = useState<boolean>(false);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);

    useEffect(() => {
        if (!show) return;
        loadForm();
        setShowPassword(false);
        setShowEmailPass(false);
        setShowConfirm(false);
        setErrors({});
        setServerError('');
    }, [show, userId]);

    const loadForm = async () => {
        setLoading(true);
        try {
            const model = await usersModel.userGet(userId);
            setRoles(model && model.roles ? model.roles : []);
            setSalesPeople(model && model.salesPeople ? model.salesPeople : []);
            setAgents(model && model.agents ? model.agents : []);
            setBranches(toTreeNodes(model && model.branches ? model.branches : []));
            setOfficeDepartments(toTreeNodes(model && model.officeDepartments ? model.officeDepartments : []));

            if (model) {
                setForm({
                    id: model.id,
                    fullName: model.fullName || '',
                    title: model.title || '',
                    email: model.email || '',
                    // In edit mode the password fields are masked with the sentinel.
                    emailPass: isEdit ? EMPTY_PASSWORD : '',
                    password: isEdit ? EMPTY_PASSWORD : '',
                    confirmPassword: isEdit ? EMPTY_PASSWORD : '',
                    phoneNumber: model.phoneNumber || '',
                    ipAddresses: model.ipAddresses || '',
                    status: model.status != null ? model.status : ActivityStatus._0,
                    roleId: model.roleId || '',
                    link: model.link || '',
                    // The server carries the lead types as the NewLead/UsedLead/SelectLead
                    // booleans, so derive the checkbox list from them.
                    leadTypes: [
                        ...(model.newLead ? [LeadType._0] : []),
                        ...(model.selectLead ? [LeadType._1] : []),
                        ...(model.usedLead ? [LeadType._2] : []),
                    ],
                    branchId: model.branchId,
                    branchName: model.branch ? model.branch.name : undefined,
                    officeDepartmentId: model.officeDepartmentId,
                    officeDepartmentName: model.officeDepartment ? model.officeDepartment.name : undefined,
                    salesPersonId: model.salesPersonId || '',
                    agentId: model.agentId || '',
                    whatsAppNumber: model.whatsAppNumber || '',
                    extension: model.extension || '',
                });
            } else {
                setForm(emptyForm());
            }
        } catch (e) {
            console.error('Failed to load user form', e);
            setServerError('טעינת הנתונים נכשלה');
        } finally {
            setLoading(false);
        }
    };

    const update = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

    // ---- Role-driven section visibility (mirrors systemManagement.js #RoleId change) ----
    const rid = lc(form.roleId);
    const hasRole = !!form.roleId;
    let showOfficeDept = false;
    let showBranchTree = false;
    let showLink = false;
    let showBranchDiv = false;
    if (hasRole && !MANAGER_ROLES.includes(rid)) {
        if (!LEADS_ROLES.includes(rid)) {
            showOfficeDept = true;
        } else {
            showBranchTree = true;
            showLink = true;
            showBranchDiv = true;
        }
    }
    if (hasRole && !LEAD_MANAGER_ROLES.includes(rid)) {
        showBranchDiv = true;
    }
    if (rid === ROLE.BRANCH_DEPARTMENT_MANAGER) {
        showBranchDiv = true;
    }

    const toggleLeadType = (lt: LeadType) => {
        setForm((f) => {
            const has = f.leadTypes.includes(lt);
            return { ...f, leadTypes: has ? f.leadTypes.filter((x) => x !== lt) : [...f.leadTypes, lt] };
        });
    };

    const validate = async (): Promise<boolean> => {
        const errs: { [k: string]: string } = {};

        if (!form.fullName.trim()) errs.fullName = 'שדה חובה';

        if (!form.email.trim()) {
            errs.email = 'שדה חובה';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
            errs.email = 'כתובת אימייל לא תקינה';
        }

        if (!form.password) errs.password = 'שדה חובה';
        if (!form.confirmPassword) errs.confirmPassword = 'שדה חובה';
        if (form.password && form.confirmPassword && form.password !== form.confirmPassword) {
            errs.confirmPassword = 'הסיסמאות אינן תואמות';
        }

        if (form.phoneNumber && !/^[0-9+\-\s]{6,20}$/.test(form.phoneNumber.trim())) {
            errs.phoneNumber = 'מספר טלפון לא תקין';
        }

        if (!form.roleId) errs.roleId = 'יש לבחור קבוצה';

        if (showBranchDiv && inGroup(SALESPERSON_REQUIRED, form.roleId) && !form.salesPersonId) {
            errs.salesPersonId = 'חובה לבחור מנהל מכירות';
        }
        if (showBranchTree && inGroup(BRANCH_REQUIRED, form.roleId) && !form.branchId) {
            errs.branchId = 'חובה לבחור מחלקה';
        }

        // Async uniqueness check (skip if the email itself is already invalid).
        if (!errs.email) {
            try {
                const exists = await usersModel.userCheckEmailExists(form.email.trim(), form.id);
                if (exists) errs.email = 'כתובת האימייל כבר קיימת במערכת';
            } catch (e) {
                console.error('Email exists check failed', e);
            }
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setServerError('');
        const ok = await validate();
        if (!ok) return;

        const body = new UserCreateModel();
        body.id = form.id;
        body.fullName = form.fullName.trim();
        body.title = form.title;
        body.email = form.email.trim();
        body.emailPass = form.emailPass;
        body.password = form.password;
        body.confirmPassword = form.confirmPassword;
        body.phoneNumber = form.phoneNumber;
        body.ipAddresses = form.ipAddresses;
        body.status = form.status;
        body.roleId = form.roleId;
        body.whatsAppNumber = form.whatsAppNumber;
        body.extension = form.extension;
        // Lead / sales sections — only meaningful for the roles that show them.
        if (showLink) {
            body.link = form.link;
            body.leadTypes = form.leadTypes;
        }
        // Branch picker is shown for every role, so always persist its value.
        body.branchId = form.branchId || undefined;
        // Agent select is shown for every role, so always persist its value.
        body.agentId = form.agentId || undefined;
        if (showOfficeDept) body.officeDepartmentId = form.officeDepartmentId;
        if (showBranchDiv) body.salesPersonId = form.salesPersonId || undefined;

        setSaving(true);
        try {
            await usersModel.userCreate(body);
            onSaved();
            onClose();
        } catch (err) {
            console.error('Failed to save user', err);
            setServerError('שמירת המשתמש נכשלה');
        } finally {
            setSaving(false);
        }
    };

    if (!show) return null;

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="newuser-overlay" onClick={handleOverlayClick}>
            <div className="newuser-modal" dir="rtl">
                <form id="addUserForm" autoComplete="off" onSubmit={onSubmit}>
                    <div className="header">
                        <button type="button" className="close" aria-label="Close" onClick={onClose}>
                            <span className="close-modal" />
                        </button>
                        <h3 className="title">{isEdit ? 'עריכת משתמש' : 'משתמש חדש'}</h3>
                    </div>

                    {loading ? (
                        <div className="newuser-loading">טוען…</div>
                    ) : (
                        <>
                            <div className="create-form__wrappers">
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">שם מלא</span>
                                    </div>
                                    <input
                                        className="form-control"
                                        value={form.fullName}
                                        onChange={(e) => update({ fullName: e.target.value })}
                                    />
                                    {errors.fullName && <span className="text-danger">{errors.fullName}</span>}
                                </div>
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">תפקיד</span>
                                    </div>
                                    <input
                                        className="form-control"
                                        value={form.title}
                                        onChange={(e) => update({ title: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="create-form__wrappers">
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">אימייל (שם משתמש)</span>
                                    </div>
                                    <input
                                        className="form-control"
                                        dir="ltr"
                                        value={form.email}
                                        onChange={(e) => update({ email: e.target.value })}
                                    />
                                    {errors.email && <span className="text-danger">{errors.email}</span>}
                                </div>
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">סיסמה למייל</span>
                                    </div>
                                    {!isEdit && (
                                        <span
                                            className="newuser-eye"
                                            onClick={() => setShowEmailPass((v) => !v)}
                                        >
                                            {showEmailPass ? '🙈' : '👁'}
                                        </span>
                                    )}
                                    <input
                                        className="form-control"
                                        type={showEmailPass ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        value={form.emailPass}
                                        onChange={(e) => update({ emailPass: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="create-form__wrappers">
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">סיסמא</span>
                                    </div>
                                    {!isEdit && (
                                        <span
                                            className="newuser-eye"
                                            onClick={() => setShowPassword((v) => !v)}
                                        >
                                            {showPassword ? '🙈' : '👁'}
                                        </span>
                                    )}
                                    <input
                                        className="form-control"
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        value={form.password}
                                        onChange={(e) => update({ password: e.target.value })}
                                    />
                                    {errors.password && <span className="text-danger">{errors.password}</span>}
                                </div>
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">חזרה על סיסמא</span>
                                    </div>
                                    {!isEdit && (
                                        <span
                                            className="newuser-eye"
                                            onClick={() => setShowConfirm((v) => !v)}
                                        >
                                            {showConfirm ? '🙈' : '👁'}
                                        </span>
                                    )}
                                    <input
                                        className="form-control"
                                        type={showConfirm ? 'text' : 'password'}
                                        value={form.confirmPassword}
                                        onChange={(e) => update({ confirmPassword: e.target.value })}
                                    />
                                    {errors.confirmPassword && (
                                        <span className="text-danger">{errors.confirmPassword}</span>
                                    )}
                                </div>
                            </div>

                            <div className="create-form__wrappers">
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">טלפון לאימות זיהוי</span>
                                    </div>
                                    <input
                                        className="form-control"
                                        type="text"
                                        value={form.phoneNumber}
                                        onChange={(e) => update({ phoneNumber: e.target.value })}
                                    />
                                    {errors.phoneNumber && <span className="text-danger">{errors.phoneNumber}</span>}
                                </div>
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">כניסה מ IP - </span>
                                    </div>
                                    <input
                                        className="form-control"
                                        type="text"
                                        value={form.ipAddresses}
                                        onChange={(e) => update({ ipAddresses: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="create-form__wrappers">
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">סטטוס</span>
                                    </div>
                                    <span className="arrow-down--form" />
                                    <select
                                        className="form-control"
                                        value={form.status}
                                        onChange={(e) =>
                                            update({ status: parseInt(e.target.value, 10) as ActivityStatus })
                                        }
                                    >
                                        <option value={ActivityStatus._0}>פעיל</option>
                                        <option value={ActivityStatus._1}>לא פעיל</option>
                                    </select>
                                </div>
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">קבוצה</span>
                                    </div>
                                    <span className="arrow-down--form" />
                                    <select
                                        className="form-control"
                                        value={form.roleId}
                                        onChange={(e) => update({ roleId: e.target.value })}
                                    >
                                        <option value="">בחר</option>
                                        {roles.map((r) => (
                                            <option key={r.value} value={r.value}>
                                                {r.text}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.roleId && <span className="text-danger">{errors.roleId}</span>}
                                </div>
                            </div>

                            {showLink && (
                                <div className="create-form__wrappers">
                                    <div className="col-sm">
                                        <div className="minor-title-box text-right">
                                            <span className="title">לינק</span>
                                        </div>
                                        <input
                                            className="form-control"
                                            type="text"
                                            value={form.link}
                                            onChange={(e) => update({ link: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-sm">
                                        <div className="minor-title-box text-right">
                                            <span className="title">סוג ליד</span>
                                        </div>
                                        <div className="leadtypes-box">
                                            {LEAD_TYPE_OPTIONS.map((opt) => (
                                                <label key={opt.value} className="leadtype-item">
                                                    <input
                                                        type="checkbox"
                                                        checked={form.leadTypes.includes(opt.value)}
                                                        onChange={() => toggleLeadType(opt.value)}
                                                    />
                                                    <span>{opt.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Branch ("מחלקה") and agent ("סוכן") pickers — shown for every role/user type. */}
                            <div className="create-form__wrappers">
                                <div className="col-sm">
                                    <TreeSelectInput
                                        title="מחלקה"
                                        placeholder="מחלקה"
                                        items={branches}
                                        value={form.branchId}
                                        valueName={form.branchName}
                                        onChange={(id, name) =>
                                            update({ branchId: id, branchName: name })
                                        }
                                        errorText={errors.branchId}
                                    />
                                </div>
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">סוכן</span>
                                    </div>
                                    <span className="arrow-down--form" />
                                    <select
                                        className="form-control"
                                        value={form.agentId}
                                        onChange={(e) => update({ agentId: e.target.value })}
                                    >
                                        <option value="" />
                                        {agents.map((a) => (
                                            <option key={a.value} value={a.value}>
                                                {a.text}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {showBranchDiv && (
                                <div className="create-form__wrappers">
                                    <div className="col-sm">
                                        {showOfficeDept && (
                                            <TreeSelectInput
                                                title="מחלקה"
                                                placeholder="מחלקה"
                                                items={officeDepartments}
                                                value={form.officeDepartmentId}
                                                valueName={form.officeDepartmentName}
                                                onChange={(id, name) =>
                                                    update({ officeDepartmentId: id, officeDepartmentName: name })
                                                }
                                            />
                                        )}
                                    </div>
                                    <div className="col-sm">
                                        <div className="minor-title-box text-right">
                                            <span className="title">איש מכירות</span>
                                        </div>
                                        <span className="arrow-down--form" />
                                        <select
                                            className="form-control"
                                            value={form.salesPersonId}
                                            onChange={(e) => update({ salesPersonId: e.target.value })}
                                        >
                                            <option value="" />
                                            {salesPeople.map((sp) => (
                                                <option key={sp.value} value={sp.value}>
                                                    {sp.text}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.salesPersonId && (
                                            <span className="text-danger">{errors.salesPersonId}</span>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="create-form__wrappers">
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">מספר וואטס אפ</span>
                                    </div>
                                    <input
                                        className="form-control"
                                        value={form.whatsAppNumber}
                                        onChange={(e) => update({ whatsAppNumber: e.target.value })}
                                    />
                                </div>
                                <div className="col-sm">
                                    <div className="minor-title-box text-right">
                                        <span className="title">שלוחה</span>
                                    </div>
                                    <input
                                        className="form-control"
                                        value={form.extension}
                                        onChange={(e) => update({ extension: e.target.value })}
                                    />
                                </div>
                            </div>

                            {serverError && <div className="newuser-server-error text-danger">{serverError}</div>}

                            <div className="action-buttons">
                                <div className="action-buttons__half">
                                    <button
                                        type="submit"
                                        className="btn btn-link button btn-primary button-outlined"
                                        disabled={saving}
                                    >
                                        {saving ? 'שומר…' : isEdit ? 'שמור' : 'הוסף'}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-link button button-outlined"
                                        onClick={onClose}
                                    >
                                        ביטול
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
};

export default NewUser;
